import { useQuery } from '@tanstack/react-query';
import { Suspense, useMemo, useState } from 'react';

import { Card } from '../../components/Card';
import { BarCompare, DonutShare, LineTrend } from '../../components/charts';
import { DateRangeFilter } from '../../components/DateRangeFilter';
import { ExportMenu } from '../../components/ExportMenu';
import { KpiCard } from '../../components/KpiCard';
import { PageHeader } from '../../components/PageHeader';
import {
  type BranchSalesRow,
  getBranchDetail,
  getSalesByBranch,
  listBranches,
} from '../../data/fetchers';
import { CHART_H } from '../../lib/chartTokens';
import type { ExportCol } from '../../lib/export';
import {
  fmtAmount,
  fmtBaht,
  fmtBahtShort,
  fmtSatangCompact,
} from '../../lib/format';
import { useBranchScope } from '../../lib/useBranchScope';
import { useDateRange } from '../../lib/useDateRange';

const deltaFrac = (cur: number, prev: number): number =>
  prev > 0 ? (cur - prev) / prev : cur > 0 ? 1 : 0;
const round1 = (n: number) => Math.round(n * 10) / 10;
const chartSkeleton = (
  <div
    className="animate-pulse bg-bg-warm rounded-card"
    style={{ height: CHART_H.md }}
  />
);

interface ExportRow {
  rank: number;
  branch: string;
  revenueBaht: string;
  revenueSatang: number;
  sharePct: number;
  deltaPct: number;
  perDayBaht: string;
  perDaySatang: number;
}
const exportCols: ExportCol<ExportRow>[] = [
  { key: 'rank', header: 'อันดับ' },
  { key: 'branch', header: 'สาขา' },
  { key: 'revenueBaht', header: 'ยอดขาย' },
  { key: 'revenueSatang', header: 'ยอดขาย (satang)' },
  { key: 'sharePct', header: 'สัดส่วน %' },
  { key: 'deltaPct', header: 'Δ %' },
  { key: 'perDayBaht', header: 'เฉลี่ย/วัน' },
  { key: 'perDaySatang', header: 'เฉลี่ย/วัน (satang)' },
];

export function SalesByBranchView() {
  const { scope, ready } = useBranchScope();
  const { range, prev } = useDateRange();
  const [openId, setOpenId] = useState<string | null>(null);
  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const shortOf = useMemo(() => {
    const m = new Map(branches.map((b) => [b.id, b.short]));
    return (id: string) => m.get(id) ?? id;
  }, [branches]);

  const { data, isLoading } = useQuery({
    queryKey: [
      'report',
      'by-branch',
      scope.branchId,
      scope.allowedBranchIds,
      range.fromDay,
      range.toDay,
    ],
    queryFn: () => getSalesByBranch(scope, range, prev),
    enabled: ready,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const days = data?.days ?? 1;
  const totalDelta = deltaFrac(total, data?.prevTotal ?? 0);
  const top = rows[0];

  const barData = useMemo(
    () => rows.map((r) => ({ label: shortOf(r.branchId), value: r.revenue })),
    [rows, shortOf],
  );
  const exportRows: ExportRow[] = rows.map((r, i) => ({
    rank: i + 1,
    branch: shortOf(r.branchId),
    revenueBaht: fmtBaht(r.revenue),
    revenueSatang: r.revenue,
    sharePct: total ? round1((r.revenue / total) * 100) : 0,
    deltaPct: round1(deltaFrac(r.revenue, r.prevRevenue) * 100),
    perDayBaht: fmtBaht(Math.round(r.revenue / days)),
    perDaySatang: Math.round(r.revenue / days),
  }));

  return (
    <div className="bo-fade">
      <PageHeader
        title="ยอดขายแยกสาขา"
        subtitle="เปรียบเทียบสาขา · สัดส่วน · การเติบโต และเจาะลึกรายสาขา"
        actions={
          <ExportMenu
            rows={exportRows}
            cols={exportCols}
            filename="ยอดขายแยกสาขา"
            title="ยอดขายแยกสาขา"
            pdf
          />
        }
      />
      <DateRangeFilter />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          label="ยอดขายรวม"
          value={fmtBaht(total)}
          tone="primary"
          delta={totalDelta}
          sub="เทียบงวดก่อน"
        />
        <KpiCard
          label="จำนวนสาขา"
          value={String(rows.length)}
          tone="accent"
          sub="ในขอบเขตที่ดูได้"
        />
        <KpiCard
          label="สาขาสูงสุด"
          value={top ? shortOf(top.branchId) : '—'}
          tone="ok"
          sub={top ? fmtBahtShort(top.revenue) : undefined}
        />
        <KpiCard
          label="เฉลี่ย/สาขา"
          value={fmtBaht(rows.length ? total / rows.length : 0)}
          tone="warn"
          sub={`${days} วัน`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card title="ยอดขายรายสาขา">
          <Suspense fallback={chartSkeleton}>
            <BarCompare
              data={barData}
              colorByIndex
              valueFormat={fmtBahtShort}
              tickFormat={fmtSatangCompact}
              showValues
            />
          </Suspense>
        </Card>
        <Card title="สัดส่วนยอดขาย">
          <Suspense fallback={chartSkeleton}>
            <DonutShare data={barData} valueFormat={fmtBahtShort} />
          </Suspense>
        </Card>
      </div>

      {!ready || isLoading ? (
        <div className="bo-card h-60 animate-pulse" />
      ) : (
        <div className="bo-card overflow-x-auto">
          <table className="bo-table">
            <thead>
              <tr>
                <th>#</th>
                <th>สาขา</th>
                <th className="bo-th-num">ยอดขาย</th>
                <th className="bo-th-num">สัดส่วน</th>
                <th className="bo-th-num">Δ เทียบงวดก่อน</th>
                <th className="bo-th-num">เฉลี่ย/วัน</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-ink-3 py-10">
                    ไม่มียอดขายในช่วงเวลานี้
                  </td>
                </tr>
              ) : (
                rows.map((r, i) => (
                  <BranchRow
                    key={r.branchId}
                    r={r}
                    i={i}
                    short={shortOf(r.branchId)}
                    total={total}
                    days={days}
                    open={openId === r.branchId}
                    onToggle={() =>
                      setOpenId(openId === r.branchId ? null : r.branchId)
                    }
                    range={range}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BranchRow({
  r,
  i,
  short,
  total,
  days,
  open,
  onToggle,
  range,
}: {
  r: BranchSalesRow;
  i: number;
  short: string;
  total: number;
  days: number;
  open: boolean;
  onToggle: () => void;
  range: { fromDay: number; toDay: number };
}) {
  const share = total ? (r.revenue / total) * 100 : 0;
  const d = deltaFrac(r.revenue, r.prevRevenue);
  const dCls =
    d > 0.001 ? 'text-ok' : d < -0.001 ? 'text-danger' : 'text-ink-4';
  return (
    <>
      <tr
        className={`cursor-pointer ${open ? 'bg-primary-light' : 'hover:bg-bg-warm'}`}
        onClick={onToggle}
        aria-expanded={open}
        title="คลิกเพื่อเจาะลึกรายสาขา"
      >
        <td>
          <span
            className={`inline-flex w-6 h-6 items-center justify-center rounded-btn text-sm-label font-bold ${i < 3 ? 'bg-primary text-white' : 'bg-bg-warm text-ink-2'}`}
          >
            {i + 1}
          </span>
        </td>
        <td className="font-semibold">{short}</td>
        <td className="bo-td-num num font-semibold">{fmtAmount(r.revenue)}</td>
        <td className="bo-td-num">
          <span className="inline-flex items-center gap-2 justify-end">
            <span className="relative h-1.5 w-[70px] rounded-full bg-line-2 overflow-hidden hidden sm:inline-block">
              <span
                className="absolute inset-y-0 left-0 rounded-full bg-primary"
                style={{ width: `${Math.min(100, share)}%` }}
              />
            </span>
            <span className="num">{share.toFixed(1)}%</span>
          </span>
        </td>
        <td className={`bo-td-num num font-bold ${dCls}`}>
          {d > 0 ? '▲ +' : d < 0 ? '▼ ' : '– '}
          {Math.abs(d * 100).toFixed(1)}%
        </td>
        <td className="bo-td-num num text-ink-3">
          {fmtAmount(Math.round(r.revenue / days))}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6} className="!p-0 bg-surface-2 border-b border-line-2">
            <BranchDrilldown branchId={r.branchId} name={short} range={range} />
          </td>
        </tr>
      )}
    </>
  );
}

function BranchDrilldown({
  branchId,
  name,
  range,
}: {
  branchId: string;
  name: string;
  range: { fromDay: number; toDay: number };
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['branch-detail', branchId, range.fromDay, range.toDay],
    queryFn: () => getBranchDetail(branchId, range),
  });
  if (isLoading || !data)
    return <div className="h-40 m-4 animate-pulse bg-bg-warm rounded-card" />;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-4">
      <div>
        <div className="text-sm-label font-bold text-ink-3 mb-2">
          แนวโน้มรายวัน — {name}
        </div>
        <Suspense fallback={chartSkeleton}>
          <LineTrend
            data={data.series}
            height={CHART_H.sm}
            valueFormat={fmtBahtShort}
            tickFormat={fmtSatangCompact}
          />
        </Suspense>
      </div>
      <div>
        <div className="text-sm-label font-bold text-ink-3 mb-2">
          สินค้าขายดีในสาขา (ยอดเงิน)
        </div>
        <Suspense fallback={chartSkeleton}>
          <BarCompare
            data={data.topProducts.map((p) => ({
              label: p.name,
              value: p.amount,
            }))}
            height={CHART_H.sm}
            colorByIndex
            valueFormat={fmtBahtShort}
            tickFormat={fmtSatangCompact}
          />
        </Suspense>
      </div>
    </div>
  );
}
